/**
 * "Remix" — the modal every Remix button opens (trend card, stage, song drawer). Header: the song (cover or genre tile,
 * title, artist · genre, rank + status) with the "TikTok only" chip and one line on what Remix makes; then a calm info
 * block — the rights for the remix (always visible) and a collapsible "How it works" (copy in rights.ts). Body: an optional
 * "any other instructions" textarea (sent as `extra_prompt`) and two compact settings — length (6 / 15 / 30 / 60s,
 * starting from the prompt bar's remembered length) and Instrumental / Vocals. Footer: Cancel + Generate (the dialog's
 * only solid button; ⌘/Ctrl+Enter submits).
 * The remix is made for the active brand ("For <brand>" under the song; sent as `brand_id`).
 * Generate calls the same lookalike endpoint the prompt bar used (POST /trends/audio/{id}/lookalike with region /
 * period) and hands the accepted job to `onStarted`, which registers it with the page's jobs store so it appears at the
 * top of Recent creations. On success the dialog closes with a quiet toast; on error it stays open with the text intact and
 * the error inline.
 */
import { useRef, useState, type KeyboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AudioTrend } from '@/api/audioStudioApi';
import { ChevronRight, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { studioErrorText, capReachedOf, accessClosedOf } from '@/cowork/launch/studioErrors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Segmented, labelCls } from './CreateOptions';
import { BrandAvatar, useStudioBrands } from './brands';
import { GenreTile } from './CoverArt';
import { AD_DURATIONS, LOOKALIKE_PROMPT_MAX, buildLookalikeBody, loadInitialForm, type RemixSettings } from './createForm';
import { startJobRequest, type StudioJobRef, type StudioJobRequest } from './studioJobs';
import { MUSIC_RIGHTS_TERMS_LABEL, REMIX_HOW_IT_WORKS, REMIX_NOTE, REMIX_RIGHTS, remixRightsItems } from './rights';
import { RankChange, RightsChip, TrendStatusText } from './TrendBits';
import { trendGenres, type TrendFocus } from './trendInsights';

const PLACEHOLDER = 'Any other instructions? e.g. brighter, add a whistle hook, for a skincare launch';

export function RemixDialog({ focus, onOpenChange, onStarted }: {
  /** The song to remix; null = closed. */
  focus: TrendFocus | null;
  onOpenChange: (open: boolean) => void;
  /** The job was accepted — the page adds it to Recent creations. */
  onStarted: (job: StudioJobRef) => void;
}) {
  // After a remix starts, focus is NOT returned to the Remix button: that would scroll the page back to the card while
  // it is on its way to Recent creations.
  const started = useRef(false);
  return (
    <Dialog open={Boolean(focus)} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] gap-5 overflow-y-auto rounded-[12px] sm:max-w-[480px]"
        onOpenAutoFocus={() => { started.current = false; }}
        onCloseAutoFocus={(e) => { if (started.current) e.preventDefault(); }}
      >
        {/* Keyed so the text, settings and error start fresh for every song. */}
        {focus && <RemixForm key={focus.trend.trend_id} focus={focus} onClose={() => onOpenChange(false)} onStarted={(job) => { started.current = true; onStarted(job); }} />}
      </DialogContent>
    </Dialog>
  );
}

function RemixForm({ focus, onClose, onStarted }: { focus: TrendFocus; onClose: () => void; onStarted: (job: StudioJobRef) => void }) {
  const t = focus.trend;
  const genres = trendGenres(t);
  const cover = focus.placeholderCover ? null : t.cover_url;
  const { activeBrand } = useStudioBrands();
  // Length and vocals start from what the prompt bar last remembered (it saves them on every change).
  const [settings, setSettings] = useState<RemixSettings>(() => {
    const f = loadInitialForm();
    return { duration: f.duration, vocals: f.vocals, extraPrompt: '' };
  });

  const remix = useMutation({
    mutationFn: (request: StudioJobRequest) => startJobRequest(request),
    onSuccess: (job, request) => {
      onStarted({ jobId: job.job_id, label: `Remix of “${t.title}”`, trendTitle: t.title, durationS: settings.duration, request });
      toast(`Remixing “${t.title}”: see Recent creations`);
      onClose();
    },
  });

  const submit = () => {
    if (remix.isPending) return;
    remix.mutate({ kind: 'lookalike', trendId: t.trend_id, body: { ...buildLookalikeBody(settings), ...(activeBrand ? { brand_id: activeBrand.brandId } : {}) }, where: { region: focus.region, period: focus.period } });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); }
  };

  return (
    <form className="contents" aria-label={`Remix ${t.title}`} onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <DialogHeader className="gap-3 pr-6 text-left">
        <div className="flex items-start gap-3">
          <span className="h-12 w-12 shrink-0 overflow-hidden rounded-[8px] bg-[#f2f4f7]">
            {cover
              ? <img src={cover} alt="" className="h-full w-full object-cover" />
              : <GenreTile genre={genres[0] ?? null} title={t.title} size="thumb" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11.5px] leading-4 text-[#6941c6]" style={{ fontWeight: 600 }}>Remix</div>
            <DialogTitle className="truncate text-[16px] leading-6 text-[#101828]" title={t.title}>{t.title}</DialogTitle>
            <p className="truncate text-[12.5px] leading-[18px] text-[#667085]">{[t.artist || 'Unknown artist', genres[0]].filter(Boolean).join(' · ')}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11.5px] leading-4 text-[#667085]">
              <span className="inline-flex items-center gap-1 tabular-nums" style={{ fontWeight: 600 }}>#{t.rank} <RankChange v={t.rank_change} /></span>
              <TrendStatusText status={t.trend_status} />
              {t.cml_cleared && <RightsChip kind="tiktok" />}
            </div>
            {activeBrand && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[12px] leading-4 text-[#475467]" data-remix-brand>
                For <BrandAvatar brand={activeBrand} size={14} /> <span style={{ fontWeight: 600 }}>{activeBrand.brandName}</span>
              </p>
            )}
          </div>
        </div>
        <DialogDescription className="text-[12.5px] leading-[18px] text-[#475467]">{REMIX_NOTE}</DialogDescription>
      </DialogHeader>

      <RemixRightsInfo trend={t} />

      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="remix-extra" className="sr-only">Any other instructions (optional)</label>
          <Textarea
            id="remix-extra"
            autoFocus
            rows={3}
            maxLength={LOOKALIKE_PROMPT_MAX}
            value={settings.extraPrompt}
            onChange={(e) => setSettings((s) => ({ ...s, extraPrompt: e.target.value.slice(0, LOOKALIKE_PROMPT_MAX) }))}
            onKeyDown={onKeyDown}
            placeholder={PLACEHOLDER}
            className="min-h-0 resize-none rounded-[8px] text-[14px] leading-5"
          />
          {settings.extraPrompt.length >= LOOKALIKE_PROMPT_MAX * 0.8 && (
            <p className="mt-1 text-right text-[11.5px] leading-4 tabular-nums text-[#98a2b3]" aria-live="polite">{settings.extraPrompt.length}/{LOOKALIKE_PROMPT_MAX}</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className={labelCls} style={{ fontWeight: 600 }}>Length</div>
            <Segmented label="Length" size="sm" value={settings.duration} options={AD_DURATIONS} onChange={(v) => setSettings((s) => ({ ...s, duration: v }))} format={(v) => `${v}s`} />
          </div>
          <div>
            <div className={labelCls} style={{ fontWeight: 600 }}>Vocals</div>
            <Segmented
              label="Vocals"
              size="sm"
              value={settings.vocals ? 'vocals' : 'instrumental'}
              options={['instrumental', 'vocals'] as const}
              onChange={(v) => setSettings((s) => ({ ...s, vocals: v === 'vocals' }))}
              format={(v) => (v === 'vocals' ? 'Vocals' : 'Instrumental')}
            />
          </div>
        </div>
        {remix.isError && (
          <p role="alert" className="rounded-[8px] bg-[#fef3f2] px-3 py-2 text-[12.5px] leading-[18px] text-[#b42318]">
            {capReachedOf(remix.error) || accessClosedOf(remix.error) ? studioErrorText(remix.error, 'audio') : `${(remix.error as Error).message || 'Could not start the remix'}, try again.`}
          </p>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="ghost" onClick={onClose} className="h-10 rounded-[8px] px-4 text-[14px] text-[#475467]">Cancel</Button>
        <Button type="submit" disabled={remix.isPending} className="h-10 gap-1.5 rounded-[8px] bg-[#be43ff] px-4 text-[14px] text-white hover:bg-[#a835e0]">
          {remix.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Wand2 className="h-4 w-4" aria-hidden />}
          Generate
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Rights for the remix (visible by default) + "How it works" (collapsed), on a quiet neutral surface. */
function RemixRightsInfo({ trend }: { trend: AudioTrend }) {
  return (
    <section aria-labelledby="remix-rights-title" className="rounded-[8px] bg-[#f9fafb] px-3 py-2.5 text-[12px] leading-[18px] text-[#475467]">
      <h3 id="remix-rights-title" className="text-[12px] leading-[18px] text-[#344054]" style={{ fontWeight: 600 }}>{REMIX_RIGHTS.title}</h3>
      <ul className="mt-1 flex list-disc flex-col gap-1 pl-4 marker:text-[#98a2b3]">
        {remixRightsItems(trend).map((r) => (
          <li key={r.label}><span className="text-[#344054]" style={{ fontWeight: 500 }}>{r.label}</span>: {r.text}</li>
        ))}
      </ul>
      <details className="group mt-2">
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-[#344054] [&::-webkit-details-marker]:hidden" style={{ fontWeight: 600 }}>
          <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" aria-hidden />
          {REMIX_HOW_IT_WORKS.title}
        </summary>
        <ol aria-label={REMIX_HOW_IT_WORKS.title} className="mt-1 flex list-decimal flex-col gap-1 pl-5 marker:text-[#98a2b3]">
          {REMIX_HOW_IT_WORKS.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </details>
      {/* TODO(legal): link to Beatly music rights terms */}
      <p className="mt-2 text-[11.5px] leading-4 text-[#98a2b3] underline decoration-dotted underline-offset-2">{MUSIC_RIGHTS_TERMS_LABEL}</p>
    </section>
  );
}
